// Recursively inject compact number formatting ("~s": SI prefix, e.g. 14M, 50K)
// on any quantitative axis encoding that does not already have an explicit format.
// Only touches `type: "quantitative"` channels so temporal/ordinal axes are unaffected.
export function injectCompactAxisFormat(spec: Record<string, unknown>): void {
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const enc = encoding[channel] as Record<string, unknown> | undefined;
      if (enc && enc.type === 'quantitative') {
        const axis = (enc.axis as Record<string, unknown> | null) ?? {};
        if (!axis.format) {
          enc.axis = { ...axis, format: '~s' };
        }
      }
    }
  }

  for (const key of ['layer', 'concat', 'hconcat', 'vconcat', 'spec']) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const sub of child) {
        if (typeof sub === 'object' && sub !== null) {
          injectCompactAxisFormat(sub as Record<string, unknown>);
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectCompactAxisFormat(child as Record<string, unknown>);
    }
  }
}

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DATE_FORMAT_DIRECTIVE = /%(?:a|A|b|B|d|e|g|G|j|m|q|u|U|V|w|W|x|y|Y)/;
const TIME_FORMAT_DIRECTIVE = /%(?:c|f|H|I|L|M|p|Q|s|S|X)/;
const DATE_ONLY_VALUE = /^\d{4}-\d{2}-\d{2}$/;
const TEMPORAL_TICK_PROPERTIES = ['tickCount', 'tickMinStep', 'values'];

const hasDateOnlyFormat = (format: unknown): format is string =>
  typeof format === 'string' &&
  DATE_FORMAT_DIRECTIVE.test(format) &&
  !TIME_FORMAT_DIRECTIVE.test(format);

const getInlineDataRows = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
): Record<string, unknown>[] => {
  const data = spec.data;
  if (data === undefined) return inheritedRows;

  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return [];
  }

  const values = (data as Record<string, unknown>).values;
  if (!Array.isArray(values)) return [];

  if (
    !values.every(
      (value): value is Record<string, unknown> =>
        typeof value === 'object' && value !== null && !Array.isArray(value),
    )
  ) {
    return [];
  }

  return values;
};

const hasDateOnlyValues = (
  field: unknown,
  rows: Record<string, unknown>[],
): boolean =>
  typeof field === 'string' &&
  rows.length > 0 &&
  rows.every(row =>
    typeof row[field] === 'string'
      ? DATE_ONLY_VALUE.test(row[field] as string)
      : false,
  );

const injectUtcScale = (temporalEncoding: Record<string, unknown>): void => {
  const scale = temporalEncoding.scale;
  if (scale === undefined) {
    temporalEncoding.scale = { type: 'utc' };
  } else if (
    typeof scale === 'object' &&
    scale !== null &&
    !Array.isArray(scale) &&
    !('type' in scale)
  ) {
    temporalEncoding.scale = {
      ...(scale as Record<string, unknown>),
      type: 'utc',
    };
  }
};

const normalizeDateOnlyTemporalAxesInSpec = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
): void => {
  const rows = getInlineDataRows(spec, inheritedRows);
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y']) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const temporalEncoding = encodedField as Record<string, unknown>;
      const axis = temporalEncoding.axis;
      if (
        temporalEncoding.type !== 'temporal' ||
        typeof axis !== 'object' ||
        axis === null
      ) {
        continue;
      }

      const temporalAxis = axis as Record<string, unknown>;
      if (!hasDateOnlyFormat(temporalAxis.format)) continue;

      if (hasDateOnlyValues(temporalEncoding.field, rows)) {
        injectUtcScale(temporalEncoding);
      }

      const hasExplicitTickConfiguration = TEMPORAL_TICK_PROPERTIES.some(
        property => property in temporalAxis,
      );
      if (!hasExplicitTickConfiguration) {
        temporalAxis.tickMinStep = DAY_IN_MILLISECONDS;
      }
    }
  }

  for (const key of ['layer', 'concat', 'hconcat', 'vconcat', 'spec']) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const subSpec of child) {
        if (typeof subSpec === 'object' && subSpec !== null) {
          normalizeDateOnlyTemporalAxesInSpec(
            subSpec as Record<string, unknown>,
            rows,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      normalizeDateOnlyTemporalAxesInSpec(
        child as Record<string, unknown>,
        rows,
      );
    }
  }
};

export function normalizeDateOnlyTemporalAxes(
  spec: Record<string, unknown>,
): void {
  normalizeDateOnlyTemporalAxesInSpec(spec);
}

const pathMarkTypes = ['line', 'area'];

const hasConfiguredPoints = (
  config: Record<string, unknown> | undefined,
  markType: string,
): boolean => {
  const markConfig = config?.[markType];
  return (
    typeof markConfig === 'object' &&
    markConfig !== null &&
    'point' in markConfig
  );
};

const hasShapeEncoding = (spec: Record<string, unknown>): boolean => {
  const encoding = spec.encoding;
  return (
    typeof encoding === 'object' && encoding !== null && 'shape' in encoding
  );
};

const enableMarkTooltip = (
  spec: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): void => {
  const markValue = spec.mark;
  let mark: Record<string, unknown> | undefined;
  if (typeof markValue === 'string') {
    mark = { type: markValue };
  } else if (typeof markValue === 'object' && markValue !== null) {
    mark = markValue as Record<string, unknown>;
  }

  if (!mark) return;

  const markType = String(mark.type);
  mark.tooltip ??= true;
  if (
    pathMarkTypes.includes(markType) &&
    mark.point === undefined &&
    !hasShapeEncoding(spec) &&
    !hasConfiguredPoints(config, markType)
  ) {
    mark.point = 'transparent';
  }
  spec.mark = mark;
};

export const injectTooltipInteractivity = (
  spec: Record<string, unknown>,
): void => {
  const isVegaLite =
    typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite');
  if (!isVegaLite) return;

  const config =
    typeof spec.config === 'object' && spec.config !== null
      ? (spec.config as Record<string, unknown>)
      : undefined;

  if (Array.isArray(spec.layer)) {
    for (const layer of spec.layer) {
      if (typeof layer === 'object' && layer !== null) {
        enableMarkTooltip(layer as Record<string, unknown>, config);
      }
    }
    return;
  }

  enableMarkTooltip(spec, config);
};
